
import { Component, Emit, InjectReactive, Vue } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class';

import OrderTotals from '@/components/OrderTotals.vue';
import ProductsSummary from '@/components/ProductsSummary.vue';
import UpsellModal from '@/components/UpsellModal.vue';
import { OrderMethods, Views } from '@/enums';
import BasketModule from '@/store/modules/basket';
import { addHoursToDate, hideCMSContent, parseDate } from '@/utils/global.util';

@Component({
  components: {
    OrderTotals,
    ProductsSummary
  },
  metaInfo: {
    title: 'Cousins Subs',
    titleTemplate: '%s - Order - Review Order'
  }
})
export default class OrderSummary extends Vue {
  @InjectReactive('progressRef') progressRef: any;
  @InjectReactive('isMobile') isMobile: boolean;

  @Getter activeRestaurant: Restaurant;
  @Getter basketUpsellItems!: BasketUpsellGroup;
  @Getter hasBeenUpsold: boolean;
  @Getter editIsOpen: boolean;
  @Action fetchUpsellItems: ({ basketId }: { basketId: string }) => void;

  @Emit('toggle-review-drawer')
  toggleReviewDrawer(ev) {
    return ev;
  }

  get hasBasket() {
    return BasketModule.id;
  }

  async mounted() {
    this.$scrollTo(this.progressRef as Element);
    if (!this.hasBasket) {
      if (this.$route.name === 'OrderSummary') {
        this.$router.push(
          {
            name: 'SelectLocation',
            params: {
              ...this.$route.params,
              location: this.activeRestaurant.slug,
            }
          });
      }
    } else {
      await this.fetchUpsellItems({ basketId: BasketModule.id });
    }
  }

  get currentBasket() {
    return BasketModule.current || {};
  }

  handleCheckoutClick() {
    const test = Promise.resolve(BasketModule.validateBasket({ basketId: BasketModule.id }));
    test.then(() => {
      this.toggleReviewDrawer(false);
      if (this.basketUpsellItems.groups.length > 0) {
        this.$modal.show(
          UpsellModal, {
            isMobile: this.isMobile,
          }, {
            clickToClose: true,
            scrollable: true,
            focusTrap: true,
            height: 'auto',
            width: this.isMobile ? '90%' : '50%',
          }, {
            'before-close': this.moveToCheckout
          }
        );
      } else {
        this.moveToCheckout();
      }
    }).catch((error) => {
      this.$log.error(error);
      this.$toasted.error(error.data);
    })
  }

  moveToCheckout() {
    hideCMSContent();

    this.$router.push(
      {
        name: 'Checkout',
        params: {
          ...this.$route.params,
          location: this.activeRestaurant.slug,
        }
      });
  }

  handleContinueShopping() {
    this.toggleReviewDrawer(false);

    hideCMSContent();

    if (![Views.CHOOSE_FOOD, Views.CHOOSE_FOOD_CATEGORY].includes(this.$route.name as Views)) {
      this.$router.push(
        {
          name: 'ChooseFood',
          params: {
            ...this.$route.params,
            location: this.activeRestaurant.slug,
          }
        });
    }
  }

  get showTitle() {
    return ![Views.ORDER_SUMMARY].includes(this.$route.name as Views);
  }

  get orderMethods() {
    return Object.values(OrderMethods);
  }

  get deliveryMode() {
    return BasketModule?.deliveryMode || '';
  }

  get isDelivery() {
    return BasketModule?.isDelivery;
  }

  get isCurbside() {
    return this.deliveryMode === OrderMethods.CURBSIDE;
  }

  get isPickup() {
    return this.deliveryMode === OrderMethods.PICKUP;
  }

  get today(): Date {
    // return new Date('2021-09-11T12:10:00'); // used to spoof times for testing
    return new Date();
  }

  get tomorrow(): Date {
    return addHoursToDate(this.today, 24);
  }

  get todayValue(): string {
    return `${this.today.getFullYear()}-${this.today.getMonth() + 1}-${this.today.getDate()}`;
  }

  get tomorrowValue(): string {
    return `${this.tomorrow.getFullYear()}-${this.tomorrow.getMonth() + 1}-${this.tomorrow.getDate()}`;
  }

  get readyTime() {
    if (this.currentBasket) {
      return (this.currentBasket.timeMode === 'advance' ? this.currentBasket.timeWanted : this.currentBasket.earliestReadyTime)
    }
    return null;
  }

  get readyTimeText() {
    if (this.readyTime) {
      const parsedDate = parseDate(this.readyTime) as Date;
      const parsedDateValue = `${parsedDate.getFullYear()}-${parsedDate.getMonth() + 1}-${parsedDate.getDate()}`;
      if (parsedDateValue === this.todayValue) {
        return `Ready at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else if (parsedDateValue === this.tomorrowValue) {
        return `Ready tomorrow at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      } else {
        return `Ready ${parsedDate.getMonth() + 1}/${parsedDate.getDate()} at ${parsedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      }
    }
    return null;
  }
}
